import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Bell, Clock, Mail, MessageSquare, AlertTriangle, Calendar,
  Play, RefreshCw, CheckCircle, XCircle, History, Settings
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export default function NotificationSchedulerPage() {
  const { axiosAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get('/scheduler/status'),
        api.get('/scheduler/history?limit=20'),
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data);
    } catch (err) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const runScheduler = async () => {
    setRunning(true);
    try {
      const res = await api.post('/scheduler/risk-alerts?force=true');
      if (res.data.status === 'started') {
        toast.success('Scheduler started! Notifications are being processed in the background.');
      } else {
        toast.info(res.data.reason || 'Scheduler skipped');
      }
      setTimeout(fetchData, 3000); // Refresh after 3 seconds
    } catch (err) {
      toast.error('Failed to run scheduler');
    }
    setRunning(false);
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-scheduler-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Bell className="text-teal-600" /> Notification Scheduler
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Automated risk alerts and court date reminders
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="rounded-full gap-2">
            <RefreshCw size={16} /> Refresh
          </Button>
          <Button 
            onClick={runScheduler} 
            disabled={running}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2"
          >
            <Play size={16} className={running ? 'animate-spin' : ''} /> 
            {running ? 'Running...' : 'Run Now'}
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      {status && (
        <div className="grid md:grid-cols-3 gap-4">
          {/* Risk Alerts Status */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <AlertTriangle className="text-orange-500" size={18} /> Risk Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Frequency</span>
                  <span className="font-medium">{status.risk_alerts.frequency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Last Run</span>
                  <span className="font-medium">
                    {status.risk_alerts.last_run ? formatDate(status.risk_alerts.last_run) : 'Never'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Recent Alerts (48h)</span>
                  <Badge className="bg-orange-100 text-orange-700">{status.risk_alerts.recent_alerts_sent}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Court Reminders Status */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Calendar className="text-slate-600" size={18} /> Court Reminders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Reminder Days</span>
                  <span className="font-medium">3 days & 1 day before</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Recent Reminders (48h)</span>
                  <Badge className="bg-slate-100 text-slate-700">{status.court_reminders.recent_reminders_sent}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Channels Status */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Settings className="text-violet-600" size={18} /> Channels
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Bell size={14} /> In-App
                  </span>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle size={12} className="mr-1" /> Active
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Mail size={14} /> Email
                  </span>
                  <Badge className={status.channels.email ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {status.channels.email ? (
                      <><CheckCircle size={12} className="mr-1" /> Active</>
                    ) : (
                      <><Clock size={12} className="mr-1" /> Mocked</>
                    )}
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <MessageSquare size={14} /> SMS
                  </span>
                  <Badge className={status.channels.sms ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {status.channels.sms ? (
                      <><CheckCircle size={12} className="mr-1" /> Active</>
                    ) : (
                      <><Clock size={12} className="mr-1" /> Mocked</>
                    )}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Banner */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-teal-50 to-emerald-50 border-l-4 border-l-teal-500">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Clock className="text-teal-600 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-teal-900">Scheduler Configuration</p>
              <p className="text-sm text-teal-700 mt-1">
                Risk alerts run every 2 days at 10:30 AM Central. High/Critical risk participants trigger notifications to their assigned peer specialist. 
                Court reminders are sent 3 days and 1 day before scheduled dates.
              </p>
              {!status?.channels.email && (
                <p className="text-sm text-amber-700 mt-2">
                  <strong>Note:</strong> Email notifications are mocked. Add SENDGRID_API_KEY to enable real emails.
                </p>
              )}
              {!status?.channels.sms && (
                <p className="text-sm text-amber-700 mt-1">
                  <strong>Note:</strong> SMS notifications are mocked. Add TELNYX_API_KEY to enable real SMS.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* History Tabs */}
      <Tabs defaultValue="risk" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="risk" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Risk Alerts ({history?.risk_notifications?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="court" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Court Reminders ({history?.court_reminders?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Risk Alerts History */}
        <TabsContent value="risk" className="space-y-3">
          {history?.risk_notifications?.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No risk alerts sent yet.</p>
              </CardContent>
            </Card>
          ) : (
            history?.risk_notifications?.map(notif => (
              <Card key={notif.id} className={`border-0 shadow-sm rounded-2xl border-l-4 ${notif.risk_level === 'critical' ? 'border-l-red-500' : 'border-l-orange-500'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${notif.risk_level === 'critical' ? 'bg-red-100' : 'bg-orange-100'}`}>
                        <AlertTriangle className={notif.risk_level === 'critical' ? 'text-red-600' : 'text-orange-600'} size={20} />
                      </div>
                      <div>
                        <p className="font-medium">{notif.participant_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Score: {notif.risk_score}/100 • {formatDate(notif.created_at)}
                        </p>
                      </div>
                    </div>
                    <Badge className={notif.risk_level === 'critical' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                      {notif.risk_level}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Court Reminders History */}
        <TabsContent value="court" className="space-y-3">
          {history?.court_reminders?.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No court reminders sent yet.</p>
              </CardContent>
            </Card>
          ) : (
            history?.court_reminders?.map(reminder => (
              <Card key={reminder.id} className="border-0 shadow-sm rounded-2xl border-l-4 border-l-slate-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Calendar className="text-slate-600" size={20} />
                      </div>
                      <div>
                        <p className="font-medium">Participant {reminder.participant_id?.slice(0, 8)}...</p>
                        <p className="text-sm text-muted-foreground">
                          {reminder.days_before} day{reminder.days_before > 1 ? 's' : ''} before • {formatDate(reminder.sent_at)}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-slate-100 text-slate-700">Sent</Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
